/**
 * EthicsGlobal Widget - Isolated API Client
 *
 * API client for the widget that doesn't depend on localStorage for tenant.
 * Takes domain as a parameter instead.
 */

import axios from 'axios';

// Default API host
const DEFAULT_HOST = "https://api.ethicsglobal.com/";

// Storage keys with namespace
const STORAGE_KEYS = {
  lang: 'eg_lang',
  domain: 'eg_domain'
};

/**
 * Create an isolated API client for a specific domain
 * @param {string} domain - The tenant domain
 * @param {string} baseURL - Optional custom base URL
 * @returns {Object} API client with methods
 */
export const createWidgetApiClient = (domain, baseURL = DEFAULT_HOST) => {
  const apiCall = axios.create({ baseURL });

  // Store domain in namespaced key
  const setDomain = (newDomain) => {
    try {
      localStorage.setItem(STORAGE_KEYS.domain, newDomain);
    } catch (e) {
      // localStorage not available
    }
  };

  // Get stored domain or use provided one
  const getDomain = () => {
    try {
      return localStorage.getItem(STORAGE_KEYS.domain) || domain;
    } catch (e) {
      return domain;
    }
  };

  // Initialize domain storage
  if (domain) {
    setDomain(domain);
  }

  return {
    /**
     * Get the current form configuration
     * @param {string} lang - Language code
     * @returns {Promise<Object>} Form configuration
     */
    getDetailCurrentReport: async (lang) => {
      const tenant = getDomain();
      const pathLang = lang != null ? `?lg=${lang}` : '';
      try {
        const resp = await apiCall.get(`${tenant}/api/form/retrieve/current-report/${pathLang}`);
        return resp.data;
      } catch (error) {
        console.error('[EthicsGlobal Widget] Error fetching form:', error);
        throw error;
      }
    },

    /**
     * Get form by identifier name
     * @param {string} lang - Language code
     * @param {string} name - Form identifier
     * @returns {Promise<Object>} Form configuration
     */
    getDetailReportByName: async (lang, name) => {
      const tenant = getDomain();
      const pathLang = lang != null ? `?lg=${lang}` : '';
      try {
        const resp = await apiCall.get(`${tenant}/api/form/retrieve/by-identifier/${name}/${pathLang}`);
        return resp.data;
      } catch (error) {
        console.error('[EthicsGlobal Widget] Error fetching form by name:', error);
        throw error;
      }
    },

    /**
     * Create a report submission
     * @param {Object} report - Report data
     * @param {string} captchaToken - reCAPTCHA token
     * @param {string} identifier - Form identifier
     * @returns {Promise<Object>} Created report response
     */
    createReport: async (report = {}, captchaToken = '', identifier = "report") => {
      const tenant = getDomain();
      try {
        const reportWithCaptcha = {
          ...report,
          event: {
            siteKey: "6LdCZ6wqAAAAADNDLaiULssUYnuqIx0MePGwJ_2N",
            token: captchaToken,
          },
        };
        const resp = await apiCall.post(`${tenant}/api/report/create/?identifier=${identifier}`, reportWithCaptcha);
        return resp.data;
      } catch (error) {
        console.error('[EthicsGlobal Widget] Error creating report:', error);
        throw error;
      }
    },

    /**
     * Get report status by tracking code
     * @param {string} trackingCode - Tracking code
     * @returns {Promise<Object|false>} Report status or false
     */
    getStatusReport: async (trackingCode) => {
      const tenant = getDomain();
      try {
        const resp = await apiCall.post(`${tenant}/api/report/status/get/`, { tracking_code: trackingCode });
        return resp.data;
      } catch (error) {
        console.error('[EthicsGlobal Widget] Error getting report status:', error);
        return false;
      }
    },

    /**
     * Create an attachment
     * @param {FormData} formData - File data
     * @returns {Promise<Object>} Attachment response
     */
    createAttachment: async (formData) => {
      const tenant = getDomain();
      try {
        const resp = await apiCall.post(`${tenant}/api/attachment/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return resp.data;
      } catch (error) {
        console.error('[EthicsGlobal Widget] Error creating attachment:', error);
        throw error;
      }
    },

    /**
     * Send tracking code by email
     * @param {string} email - Email address
     * @param {string} trackingCode - Tracking code
     * @returns {Promise<Object>} Response
     */
    sendTrackingByEmail: async (email, trackingCode) => {
      const tenant = getDomain();
      try {
        const resp = await apiCall.post(`${tenant}/api/report/send-tracking/`, {
          email,
          tracking_code: trackingCode
        });
        return resp.data;
      } catch (error) {
        console.error('[EthicsGlobal Widget] Error sending tracking email:', error);
        throw error;
      }
    },

    /**
     * Get catalogue data
     * @param {string} catalogueId - Catalogue ID
     * @param {string} lang - Language code
     * @returns {Promise<Object>} Catalogue data
     */
    getCatalogue: async (catalogueId, lang) => {
      const tenant = getDomain();
      const pathLang = lang != null ? `?lg=${lang}` : '';
      try {
        const resp = await apiCall.get(`${tenant}/api/catalogue/${catalogueId}/${pathLang}`);
        return resp.data;
      } catch (error) {
        console.error('[EthicsGlobal Widget] Error fetching catalogue:', error);
        throw error;
      }
    },

    /**
     * Get landing page configuration
     * @returns {Promise<Object>} Landing configuration
     */
    getLandingConfig: async () => {
      const tenant = getDomain();
      try {
        const resp = await apiCall.get(`${tenant}/api/landing/`);
        return resp.data;
      } catch (error) {
        console.error('[EthicsGlobal Widget] Error fetching landing config:', error);
        throw error;
      }
    },

    // Utility methods
    setDomain,
    getDomain,

    // Expose axios instance for custom requests
    axios: apiCall
  };
};

// Default export for convenience
export default createWidgetApiClient;
